import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Search, Loader2, X, Globe, ArrowRight, Check } from 'lucide-react';
import { doc, setDoc, onSnapshot } from 'firebase/firestore';
import { db } from '@/config/firebase';
import { useAuth } from '@/hooks/useAuth';
import { createCountry, createClient, createAction } from '@/services/firestoreService';
import regionMapping from '../../../assets/regiones.json';

interface SearchResultItem {
    id: string;
    name: string;
    country?: string;
    label?: string;
}

interface AddCountryModalProps {
    isOpen: boolean;
    onClose: () => void;
    onCountryAdded: () => void;
}

export function AddCountryModal({ isOpen, onClose, onCountryAdded }: AddCountryModalProps) {
    const { user } = useAuth();

    // Step State
    const [step, setStep] = useState<1 | 2>(1);

    // Step 1 Data
    const [countryId, setCountryId] = useState('');
    const [countryName, setCountryName] = useState('');
    const [regionId, setRegionId] = useState<string | null>(null);
    const [idError, setIdError] = useState<string | null>(null);

    // Step 2 Data
    const [searchTerm, setSearchTerm] = useState('');
    const [sapResults, setSapResults] = useState<SearchResultItem[]>([]);
    const [isSearchingSAP, setIsSearchingSAP] = useState(false);
    const [selectedClient, setSelectedClient] = useState<SearchResultItem | null>(null);
    const [isCreating, setIsCreating] = useState(false);
    const [creationError, setCreationError] = useState<string | null>(null);

    const inputRef = useRef<HTMLInputElement>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);

    const appId = typeof window !== 'undefined' && (window as any).__app_id ? (window as any).__app_id : 'default-app-id';

    // Reset state when opening
    useEffect(() => {
        if (isOpen) {
            setStep(1);
            setCountryId('');
            setCountryName('');
            setRegionId(null);
            setIdError(null);
            setSearchTerm('');
            setSapResults([]);
            setIsSearchingSAP(false);
            setSelectedClient(null);
            setCreationError(null);
            setTimeout(() => inputRef.current?.focus(), 100);
        }
    }, [isOpen]);

    // Validate Country ID
    const handleCountryIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const val = e.target.value.toUpperCase().slice(0, 2); // Max 2 chars
        setCountryId(val);

        if (val.length === 2) {
            const mappedRegion = (regionMapping as Record<string, string>)[val];
            if (mappedRegion) {
                setRegionId(mappedRegion);
                setIdError(null);
            } else {
                setRegionId(null);
                setIdError('Invalid Country ID (not found in regions map)');
            }
        } else {
            setRegionId(null);
            setIdError(null);
        }
    };

    const handleNext = () => {
        if (countryId && countryName && regionId) {
            setStep(2);
            setTimeout(() => searchInputRef.current?.focus(), 100);
        }
    };

    // --- Step 2 Logic (Client Search) ---

    useEffect(() => {
        if (!isOpen || step !== 2 || !user?.uid || !appId || searchTerm.length < 3) return;

        const handler = setTimeout(async () => {
            setIsSearchingSAP(true);
            const isId = /^\d+$/.test(searchTerm);

            try {
                const searchDocRef = doc(db, 'artifacts', appId, 'users', user.uid, 'frontEnd', 'customerSearch');
                await setDoc(searchDocRef, {
                    countryCode: countryId, // Use the new country ID
                    kunnr: isId ? searchTerm : "",
                    name1: isId ? "" : searchTerm.toUpperCase(),
                    searchTerm: searchTerm,
                    timestamp: new Date().toISOString()
                }, { merge: true });
            } catch (error) {
                console.error("Error writing search:", error);
                setIsSearchingSAP(false);
            }

        }, 600);

        return () => clearTimeout(handler);
    }, [searchTerm, step, user, appId, countryId, isOpen]);

    useEffect(() => {
        if (!isOpen || step !== 2 || !user?.uid || !appId) return;

        const searchDocRef = doc(db, 'artifacts', appId, 'users', user.uid, 'frontEnd', 'customerSearch');
        const unsubscribe = onSnapshot(searchDocRef, (docSnap) => {
            const data = docSnap.data();
            if (data && data.searchResults) {
                const resultsArray = Object.values(data.searchResults) as SearchResultItem[];
                setSapResults(resultsArray);
                setIsSearchingSAP(false);
            }
        });

        return () => unsubscribe();
    }, [user, appId, isOpen, step]);

    // --- Final Creation ---

    const handleCreate = async () => {
        if (!countryId || !regionId || !selectedClient) return;

        setIsCreating(true);
        setCreationError(null);

        try {
            // 1. Create Country
            await createCountry(regionId, countryId, countryName);

            // 2. Create Client
            await createClient(countryName, {
                id: selectedClient.id,
                name: selectedClient.name,
                plant: ''
            });

            // 3. Create Default Action
            await createAction(countryName, selectedClient.id, {
                status: 'Not Started',
                typeOfAction: ' ',
                notes: 'Auto-generated default action',
                picOwner: user?.email || '',
                country: countryId, // ISO Code
                countryName: countryName // Full Name
            });

            onCountryAdded();
            onClose();

        } catch (error: any) {
            console.error("Error creating country/client/action:", error);
            setCreationError(error.message || "Failed to create country setup.");
        } finally {
            setIsCreating(false);
        }
    };

    if (!isOpen) return null;

    return createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 animate-in fade-in duration-200">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg overflow-hidden animate-in zoom-in-95 duration-200 flex flex-col max-h-[85vh]">

                {/* Header */}
                <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between bg-gray-50">
                    <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                        <Globe className="w-5 h-5 text-[#eb4a4a]" />
                        Add New Country
                    </h3>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-500 transition-colors">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Progress */}
                <div className="px-6 py-2 bg-white border-b border-gray-100 flex gap-2">
                    <div className={`h-1 flex-1 rounded-full ${step >= 1 ? 'bg-[#eb4a4a]' : 'bg-gray-100'}`} />
                    <div className={`h-1 flex-1 rounded-full ${step >= 2 ? 'bg-[#eb4a4a]' : 'bg-gray-100'}`} />
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6">
                    {step === 1 ? (
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Country ID (2 chars)</label>
                                <input
                                    ref={inputRef}
                                    type="text"
                                    maxLength={2}
                                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 uppercase font-mono ${idError ? 'border-red-300 focus:ring-red-200' : 'border-gray-300 focus:ring-[#eb4a4a]'}`}
                                    placeholder="ES"
                                    value={countryId}
                                    onChange={handleCountryIdChange}
                                />
                                {idError && <p className="mt-1 text-xs text-red-500">{idError}</p>}
                                {regionId && <p className="mt-1 text-xs text-green-600 flex items-center gap-1"><Check className="w-3 h-3" /> Region found: {regionId}</p>}
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Country Name</label>
                                <input
                                    type="text"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#eb4a4a]"
                                    placeholder="Spain"
                                    value={countryName}
                                    onChange={(e) => setCountryName(e.target.value)}
                                />
                            </div>
                        </div>
                    ) : (
                        <div className="space-y-4 h-full flex flex-col">
                            {!selectedClient ? (
                                <>
                                    <p className="text-sm text-gray-600 mb-2">Search for a first client to initialize the country.</p>
                                    <div className="relative">
                                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                                        <input
                                            ref={searchInputRef}
                                            type="text"
                                            className="w-full pl-10 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#eb4a4a]"
                                            placeholder="Search Client..."
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                        />
                                        {isSearchingSAP && (
                                            <div className="absolute right-3 top-1/2 -translate-y-1/2">
                                                <Loader2 className="w-4 h-4 animate-spin text-[#eb4a4a]" />
                                            </div>
                                        )}
                                    </div>

                                    <div className="flex-1 overflow-y-auto mt-2 min-h-[200px] border rounded-lg bg-gray-50 p-2">
                                        {sapResults.map((item) => (
                                            <button
                                                key={item.id}
                                                onClick={() => setSelectedClient(item)}
                                                className="w-full text-left p-3 rounded-lg border border-transparent hover:border-[#eb4a4a]/30 hover:bg-white transition-all mb-2 shadow-sm"
                                            >
                                                <div className="font-medium text-gray-900">{item.name}</div>
                                                <div className="text-xs text-gray-500">ID: {item.id}</div>
                                            </button>
                                        ))}
                                        {sapResults.length === 0 && !isSearchingSAP && (
                                            <div className="text-center py-8 text-gray-400 text-sm">
                                                {searchTerm.length < 3 ? "Type 3+ chars to search" : "No clients found"}
                                            </div>
                                        )}
                                    </div>
                                </>
                            ) : (
                                <div className="text-center py-8">
                                    <div className="bg-green-50 text-green-700 p-4 rounded-lg inline-block text-left w-full border border-green-100">
                                        <h4 className="font-semibold flex items-center gap-2 mb-2">
                                            <Check className="w-4 h-4" /> Ready to Create
                                        </h4>
                                        <div className="text-sm space-y-1">
                                            <p><span className="font-medium text-gray-700">Country:</span> {countryName} ({countryId})</p>
                                            <p><span className="font-medium text-gray-700">Region:</span> {regionId}</p>
                                            <p><span className="font-medium text-gray-700">First Client:</span> {selectedClient.name}</p>
                                        </div>
                                        <button
                                            onClick={() => setSelectedClient(null)}
                                            className="mt-4 text-xs text-green-600 hover:text-green-800 underline"
                                        >
                                            Change Client
                                        </button>
                                    </div>
                                </div>
                            )}

                            {creationError && (
                                <div className="p-3 bg-red-50 text-red-600 text-sm rounded-lg border border-red-100">
                                    {creationError}
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex justify-end gap-3">
                    <button
                        onClick={step === 1 ? onClose : () => setStep(1)}
                        className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900"
                        disabled={isCreating}
                    >
                        {step === 1 ? 'Cancel' : 'Back'}
                    </button>

                    {step === 1 ? (
                        <button
                            onClick={handleNext}
                            disabled={!countryId || !countryName || !regionId}
                            className="px-4 py-2 text-sm font-medium text-white bg-[#eb4a4a] rounded-lg hover:bg-[#d93a3a] disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                        >
                            Next <ArrowRight className="w-4 h-4" />
                        </button>
                    ) : (
                        <button
                            onClick={handleCreate}
                            disabled={!selectedClient || isCreating}
                            className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                        >
                            {isCreating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                            Create Country
                        </button>
                    )}
                </div>
            </div>
        </div>,
        document.body
    );
}
